'use client';

import { useState, useEffect } from 'react';
import { ArrowRight, X, Zap, Eye, Check, Loader2 } from 'lucide-react';
import { useLocale } from 'next-intl';
import { Button } from '@/components/ui/Button';
import styles from './DemoFloatingCTA.module.css';

interface FreeReportFloatingCTAProps {
  reportId?: string;
  issuesCount?: number;
}

export function FreeReportFloatingCTA({ reportId, issuesCount = 10 }: FreeReportFloatingCTAProps) {
  const locale = useLocale();
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Show after a short delay for better UX
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  const handleCheckout = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // If no reportId (demo mode), redirect to homepage to run a real audit
    if (!reportId) {
      window.location.href = `/${locale}`;
      return;
    }

    try {
      const response = await fetch('/api/audit/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reportId,
          email,
          locale,
        }),
      });

      const data = await response.json();

      if (data.success && data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        setError(data.error || 'Failed to create checkout. Please try again.');
        setIsLoading(false);
      }
    } catch {
      setError('Something went wrong. Please try again.');
      setIsLoading(false);
    }
  };

  if (isDismissed) return null;

  return (
    <>
      <div className={`${styles.container} ${isVisible ? styles.visible : ''}`}>
        <div className={styles.card}>
          {/* Animated border glow */}
          <div className={styles.borderGlow} />

          {/* Dismiss button */}
          <button
            className={styles.dismissButton}
            onClick={() => setIsDismissed(true)}
            aria-label="Dismiss"
          >
            <X size={16} />
          </button>

          {/* Glow effect */}
          <div className={styles.glowEffect} />

          {/* Content */}
          <div className={styles.content}>
            {/* Left side - Badge and text */}
            <div className={styles.textContent}>
              <div className={styles.badge}>
                <Zap size={12} />
                <span>Free Preview</span>
              </div>
              <h3 className={styles.title}>
                Unlock the Full Analysis
              </h3>
              <p className={styles.description}>
                Get {issuesCount}+ more issues analyzed against 167 science-backed Audit Criteria
              </p>
            </div>

            {/* Right side - Price and CTA */}
            <div className={styles.ctaContent}>
              <div className={styles.priceBlock}>
                <span className={styles.priceLabel}>Full UX Report</span>
                <div className={styles.price}>
                  <span className={styles.currency}>$</span>
                  <span className={styles.amount}>99</span>
                </div>
              </div>

              <Button
                type="button"
                className={styles.ctaButton}
                onClick={() => {
                  setShowModal(true);
                  setError('');
                }}
              >
                <Eye size={16} />
                Get Full UX Report
                <ArrowRight size={16} />
              </Button>
            </div>
          </div>

          {/* Trust indicators */}
          <div className={styles.trustBar}>
            <span className={styles.trustItem}>Science-Backed</span>
            <span className={styles.trustDivider}>•</span>
            <span className={styles.trustItem}>167 Audit Criteria</span>
            <span className={styles.trustDivider}>•</span>
            <span className={styles.trustItem}>PDF Report</span>
          </div>
        </div>
      </div>

      {/* Email Capture Modal */}
      {showModal && (
        <div className={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <button
              className={styles.modalClose}
              onClick={() => setShowModal(false)}
              aria-label="Close"
            >
              <X size={20} />
            </button>

            <div className={styles.modalHeader}>
              <Check size={24} className={styles.modalIcon} />
              <h3 className={styles.modalTitle}>Get Full UX Report</h3>
              <p className={styles.modalSubtitle}>
                $99 one-time • Complete analysis • PDF included
              </p>
            </div>

            <form onSubmit={handleCheckout} className={styles.modalForm}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className={styles.modalInput}
                required
                autoFocus
              />

              {error && <p className={styles.modalError}>{error}</p>}

              <Button
                type="submit"
                variant="primary"
                size="md"
                style={{ width: '100%' }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 size={16} className={styles.spinner} />
                    Processing...
                  </>
                ) : (
                  'Continue to Checkout'
                )}
              </Button>

              <p className={styles.modalTerms}>
                One-time payment. You&apos;ll receive the full report with PDF download.
              </p>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
